<template>
  <div id="aoetbt">
    <h5>{{ isUpdateMode ? t('consultantCorner.walletInformation.UpdateCardExpiration') : t('checkout.CreditCartHeader') }}</h5>

    <!-- Update Mode: Only show expiration date field -->
    <div v-if="isUpdateMode" class="update-mode">
      <div id="aoetbtCreditCardExpirationDate" class="form-control" />
    </div>

    <!-- Create Mode: Show all fields -->
    <div v-else class="create-mode">
      <div id="aoetbtNameOnCard" class="form-control" />
      <div id="aoetbtCreditCardNumber" class="form-control" />
      <div id="aoetbtCreditCardCVV" class="form-control SecurityCode" />
      <div id="aoetbtCreditCardExpirationDate" class="form-control" />

      <!-- PayPal Button Section -->
      <div class="row mt-3">
        <div class="col-12 text-center">
          <div id="braintreepaypalbutton" class="paypal-button-container"></div>
        </div>
      </div>
    </div>

    <div class="row mt-3">
      <div class="col-12 text-center braintree-submit-button">
        <button id="aoetbtSubmit" class="btn primaryBtn">
          {{ isUpdateMode ? t('consultantCorner.walletInformation.UpdateExpiration') : t('buttons.AddCardToWallet') }}
        </button>
      </div>
    </div>
    <input type="hidden" id="aoetbtNonce" />
  </div>
</template>
<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { onMounted, computed } from 'vue'
import { useLogger } from 'vue-logger-plugin'

import { GetApiKey, GetCountryCode, GetOrderEntryConnectorUrl } from '@/stores/SettingsStore.ts'
import { AoeBraintree } from './AoeBraintree.ts'
import {
  BrainTreeControlFolderName,
} from '@/services/PaymentGateways/BraintreeService.ts'
import { WalletTokenCreate } from '@/api/ConsultantCorner.ts'
import { AddJavascriptToHeader } from '@/services/AoeHelpers.ts'
import type { AoeMessageEvent } from '@/types/AoeTypes'
import { GetBearerToken } from '@/stores/SettingsStore.ts'
import { aoeBraintreePaypalVault } from './AoeBraintreePaypalVault.ts'

const logger = useLogger()

// Props
const props = defineProps<{
  mode?: 'create' | 'update'
  customerCardGuid?: string
  cusWalletGuid?: string
  expirationDate?: string
}>()

// Emits
const emit = defineEmits(['wallet-added', 'wallet-updated'])

// Computed
const isUpdateMode = computed(() => props.mode === 'update')

onMounted(async () => {
  logger.debug('BraintreeWalletEntry', 'onMounted')

  window.addEventListener('message', messagehandler)

  // Load all Braintree scripts in parallel and wait for them to complete
  const scriptPromises = [
    AddJavascriptToHeader('https://js.braintreegateway.com/web/3.129.0/js/client.min.js'),
    AddJavascriptToHeader('https://js.braintreegateway.com/web/3.129.0/js/three-d-secure.min.js'),
    AddJavascriptToHeader('https://js.braintreegateway.com/web/3.129.0/js/apple-pay.min.js'),
    AddJavascriptToHeader('https://js.braintreegateway.com/web/dropin/1.45.2/js/dropin.min.js'),
    AddJavascriptToHeader('https://js.braintreegateway.com/web/3.129.0/js/hosted-fields.js'),
    AddJavascriptToHeader('https://js.braintreegateway.com/web/3.129.0/js/paypal-checkout.min.js'),
  ]

  // Fetch the client token while scripts are loading (parallel operations)
  const [, response] = await Promise.all([
    Promise.all(scriptPromises),
    WalletTokenCreate({ controlFolderName: BrainTreeControlFolderName, countryCode: await GetCountryCode() }),
  ])

  logger.debug('BraintreeWalletEntry', 'response:', response)
  if (response.parameters.ClientToken) {
    // Initialize credit card fields
    AoeBraintree.clientToken = response.parameters.ClientToken
    AoeBraintree.enableThreeDSecure = response.parameters.Check3DS === true || response.parameters.Check3DS === 'true'
    AoeBraintree.sourceValues.systemKeyword = response.parameters.SystemKeyword
    AoeBraintree.hostedFieldsText.cardholderName = t('labels.CardHolderName')
    AoeBraintree.hostedFieldsText.creditCardNumber = t('labels.CardNumber')
    AoeBraintree.hostedFieldsText.cvv = t('labels.CardSecurityCode')
    AoeBraintree.hostedFieldsText.expirationDate = t(
      'labels.CardExpirationDate',
    )

    // Set URLs and mode based on props
    AoeBraintree.isUpdateMode = isUpdateMode.value
    if (isUpdateMode.value) {
      AoeBraintree.walletExtendUrl = await GetOrderEntryConnectorUrl('WalletExtend')
      AoeBraintree.walletExtendValues.customerCardGuid = props.customerCardGuid || ''
      AoeBraintree.walletExtendValues.cusWalletGuid = props.cusWalletGuid || ''
      AoeBraintree.walletExtendValues.expirationDate = props.expirationDate || ''
    } else {
      AoeBraintree.createPaymentUrl = await GetOrderEntryConnectorUrl('WalletCreate')
    }

    AoeBraintree.apiKey = await GetApiKey()
    const bearerToken = await GetBearerToken()
    AoeBraintree.bearerToken = bearerToken
    AoeBraintree.Initialize()

    // Initialize PayPal Vault if PayPal merchant ID is available
    if (response.parameters.PaypalMerchantId) {
      logger.debug(
        'BraintreeWalletEntry',
        'Initializing PayPal Vault with merchant ID:',
        response.parameters.PaypalMerchantId,
      )

      aoeBraintreePaypalVault.paypalScriptUrl = `https://www.paypal.com/sdk/js?client-id=${response.parameters.PaypalMerchantId}`
      aoeBraintreePaypalVault.braintreeScriptUrl = 'https://js.braintreegateway.com/web/3.129.0/js/paypal-checkout.min.js'
      aoeBraintreePaypalVault.clientToken = response.parameters.ClientToken
      aoeBraintreePaypalVault.createPaymentUrl = await GetOrderEntryConnectorUrl('WalletCreate')
      aoeBraintreePaypalVault.sourceValues.systemKeyword = response.parameters.SystemKeyword
      aoeBraintreePaypalVault.Initialize()
    } else {
      logger.debug(
        'BraintreeWalletEntry',
        'PayPal merchant ID not available, hiding PayPal button',
      )
      const paypalContainer = document.getElementById('braintreepaypalbutton')
      if (paypalContainer) {
        paypalContainer.style.display = 'none'
      }
    }
  }
})

async function messagehandler(e: AoeMessageEvent) {
  logger.debug('BraintreeWalletEntry', 'messagehandler', e)
  const allowedOrigin = `${window.location.protocol}//${window.location.hostname}`
  if (!e.origin.startsWith(allowedOrigin)) {
    logger.debug(
      'BraintreeWalletEntry',
      'messagehandler',
      'origin not allowed',
      e.origin,
      allowedOrigin,
    )
    return
  }

  if (e.data && e.data.messageType === 'aoepaymentcreated') {
    logger.debug(
      'BraintreeWalletEntry',
      'messagehandler',
      'aoepaymentcreated',
      e.data.messageData,
    )
    emit('wallet-added')
  }

  if (e.data && e.data.messageType === 'aoewalletextended') {
    logger.debug(
      'BraintreeWalletEntry',
      'messagehandler',
      'aoewalletextended',
      e.data.messageData,
    )
    emit('wallet-updated')
  }

  if (e.data && e.data.messageType === 'aoewalletextenderr') {
    logger.debug(
      'BraintreeWalletEntry',
      'messagehandler',
      'aoewalletextenderr',
      e.data.messageData,
    )
    // Handle error case - could emit an error event
  }
}
</script>
<style lang="scss">
#aoetbt {
  .form-control {
    height: calc(1.5em + 0.75rem + 2px);
  }
}

.paypal-button-container {
  margin: 1rem 0;
  min-height: 45px;
}

.paypal-button-container:empty {
  display: none;
}
</style>
